import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

export type PoolFilterList = {
  inputFilterList: {
    status: string[]
    type: string[]
  }
  l2VEndpointFilterList: {
    status: string[]
  }
  ottEndpointFilterList: {
    status: string[]
  }
  pushEndpointFilterList: {
    status: string[]
  }
  recorderFilterList: {
    status: string[]
  }
  assetFilterList: {
    status: string[]
    recording: string[]
    recorder: string[]
    dateRange: { startDate: string; endDate: string } | null
  }
  recordingFilterList: {
    status: string[]
    dateRange: { startDate: string; endDate: string } | null
  }
}

export const defaultPoolFilterList: PoolFilterList = {
  assetFilterList: {
    dateRange: null,
    recorder: [],
    recording: [],
    status: []
  },
  inputFilterList: {
    status: [],
    type: []
  },
  l2VEndpointFilterList: {
    status: []
  },
  ottEndpointFilterList: {
    status: []
  },
  pushEndpointFilterList: {
    status: []
  },
  recorderFilterList: {
    status: []
  },
  recordingFilterList: {
    dateRange: null,
    status: []
  }
}

type FilterState = {
  poolFilterList: Record<string, PoolFilterList>
  setInputFilterList: (poolUuid: string, inputFilterList: PoolFilterList["inputFilterList"]) => void
  setL2VEndpointFilterList: (
    poolUuid: string,
    l2VEndpointFilterList: PoolFilterList["l2VEndpointFilterList"]
  ) => void
  setOttEndpointFilterList: (
    poolUuid: string,
    ottEndpointFilterList: PoolFilterList["ottEndpointFilterList"]
  ) => void
  setPushEndpointFilterList: (
    poolUuid: string,
    pushEndpointFilterList: PoolFilterList["pushEndpointFilterList"]
  ) => void
  setRecorderFilterList: (
    poolUuid: string,
    recorderFilterList: PoolFilterList["recorderFilterList"]
  ) => void
  setAssetFilterList: (poolUuid: string, assetFilterList: PoolFilterList["assetFilterList"]) => void
  setRecordingFilterList: (
    poolUuid: string,
    recordingFilterList: PoolFilterList["recordingFilterList"]
  ) => void
}

export const useFilterStore = create<FilterState>()(
  persist(
    set => ({
      poolFilterList: {},
      setAssetFilterList: (poolUuid, assetFilterList) =>
        set(state => ({
          poolFilterList: {
            ...state.poolFilterList,
            [poolUuid]: {
              ...(state.poolFilterList[poolUuid] ?? defaultPoolFilterList),
              assetFilterList
            }
          }
        })),
      setInputFilterList: (poolUuid, inputFilterList) =>
        set(state => ({
          poolFilterList: {
            ...state.poolFilterList,
            [poolUuid]: {
              ...(state.poolFilterList[poolUuid] ?? defaultPoolFilterList),
              inputFilterList
            }
          }
        })),
      setL2VEndpointFilterList: (poolUuid, l2VEndpointFilterList) =>
        set(state => ({
          poolFilterList: {
            ...state.poolFilterList,
            [poolUuid]: {
              ...(state.poolFilterList[poolUuid] ?? defaultPoolFilterList),
              l2VEndpointFilterList
            }
          }
        })),
      setOttEndpointFilterList: (poolUuid, ottEndpointFilterList) =>
        set(state => ({
          poolFilterList: {
            ...state.poolFilterList,
            [poolUuid]: {
              ...(state.poolFilterList[poolUuid] ?? defaultPoolFilterList),
              ottEndpointFilterList
            }
          }
        })),
      setPushEndpointFilterList: (poolUuid, pushEndpointFilterList) =>
        set(state => ({
          poolFilterList: {
            ...state.poolFilterList,
            [poolUuid]: {
              ...(state.poolFilterList[poolUuid] ?? defaultPoolFilterList),
              pushEndpointFilterList
            }
          }
        })),
      setRecorderFilterList: (poolUuid, recorderFilterList) =>
        set(state => ({
          poolFilterList: {
            ...state.poolFilterList,
            [poolUuid]: {
              ...(state.poolFilterList[poolUuid] ?? defaultPoolFilterList),
              recorderFilterList
            }
          }
        })),
      setRecordingFilterList: (poolUuid, recordingFilterList) =>
        set(state => ({
          poolFilterList: {
            ...state.poolFilterList,
            [poolUuid]: {
              ...(state.poolFilterList[poolUuid] ?? defaultPoolFilterList),
              recordingFilterList
            }
          }
        }))
    }),
    {
      name: "filters",
      storage: createJSONStorage(() => localStorage)
    }
  )
)
